import { FC, useState } from 'react';

import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import { ToucanComponents } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';

import { SettingTypes } from '~/constants/dashboardTypes';
import { useLoggedInUser } from '~/hooks/useLoggedInUser';
import { useMobileDeviceUserAgent } from '~/hooks/useMobileDeviceUserAgent';
import GraphQLMutations from '~/networking/graphqlMutations';

import EditFields from './EditFields';

const { Typography, Button, ToucanIcon } = ToucanComponents;

interface EditAccountInfoInput {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
}

interface EditAccountInfoProps {
  onClose?: () => void;
  editType: string;
  setIsEditAccountInfoForMobileShown?: (value: boolean) => void;
}

const EditAccountInfo: FC<EditAccountInfoProps> = ({ onClose, editType, setIsEditAccountInfoForMobileShown }) => {
  const theme = useTheme();
  const { loggedInUser, refreshUser } = useLoggedInUser();
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EditAccountInfoInput>({
    defaultValues: {
      name: loggedInUser && loggedInUser.name ? loggedInUser.name : '',
    },
  });
  const [updateUsersName] = useMutation(GraphQLMutations.UPDATE_USERS_NAME);
  const [updateUsersPasswordV2] = useMutation(GraphQLMutations.UPDATE_USER_PASSWORD_V2);
  const isMobileUserAgent = useMobileDeviceUserAgent();
  const [hasErrorWithCurrentPassword, setHasErrorWithCurrentPassword] = useState(false);
  const { t } = useTranslation('dashboard');

  const passwordLength = watch('newPassword')?.length ?? 0;

  const closeEditInfo = () => {
    if (isMobileUserAgent && setIsEditAccountInfoForMobileShown) {
      setIsEditAccountInfoForMobileShown(false);
    } else if (onClose) {
      onClose();
    }
  };

  const onSave = async (input: EditAccountInfoInput) => {
    setIsSaveButtonDisabled(true);
    setHasErrorWithCurrentPassword(false);
    const { name, currentPassword, newPassword } = input;

    try {
      if (editType === SettingTypes.password) {
        await updateUsersPasswordV2({ variables: { currentPassword, newPassword } });
      }
      if (editType === SettingTypes.name) {
        await updateUsersName({ variables: { name } });
      }
      refreshUser();
      closeEditInfo();
    } catch (error) {
      Sentry.setContext(`Edit ${editType} submission error`, {
        error,
      });
      if (editType === SettingTypes.password) {
        setHasErrorWithCurrentPassword(true);
        setIsSaveButtonDisabled(false);
        return;
      }
      // close component regardless of edit of name fails
      closeEditInfo();
    }
  };

  return (
    <div
      css={css`
        width: 100%;
      `}
    >
      <div>
        {isMobileUserAgent && (
          <div
            css={css`
              display: flex;
              width: 100%;
              justify-content: flex-end;
            `}
          >
            <div onClick={() => closeEditInfo()} role="button" onKeyPress={() => closeEditInfo()} tabIndex={0}>
              <ToucanIcon icon="times" />
            </div>
          </div>
        )}
        <Typography
          variant="lg"
          font="secondary"
          isBold
          css={css`
            padding-top: ${theme.spacing(2)};
            margin-bottom: ${theme.spacing(2)};
          `}
        >
          {editType === SettingTypes.name ? t('editName') : t('changePassword')}
        </Typography>
        <EditFields
          register={register}
          errors={errors}
          hasErrorWithCurrentPassword={hasErrorWithCurrentPassword}
          passwordLength={passwordLength}
          editType={editType}
        />
        <Button
          onClick={handleSubmit(onSave)}
          type="utility"
          isDisabled={isSaveButtonDisabled}
          css={css`
            width: 100%;
            margin-top: ${theme.spacing(2)};
          `}
        >
          {t('saveChanges')}
        </Button>
        <Button
          onClick={() => closeEditInfo()}
          type="secondary"
          isDisabled={isSaveButtonDisabled}
          css={css`
            width: 100%;
            margin-top: ${theme.spacing(1)};
          `}
        >
          {t('cancel')}
        </Button>
      </div>
    </div>
  );
};

export default EditAccountInfo;
